import React, { useMemo } from 'react'
import styled from 'styled-components'
import { WidgetId } from 'features/types'
import { useDashboardWidgetSetting } from 'hooks/useDashboardState'
import { useDashboardWidgetData } from 'hooks/useWidgetState'
import MediaCard from 'components/visualization/MediaCard'
import CountType from 'enums/CountType'

import { PopularMediaWidgetsType, AnnotatedMediaMetricResult, SelectedMedia } from '../../types'
import { retrieveMetrics } from '../utils/retrieveMetrics'
import {
  convertMetricToMediaCardData,
  groupMediasByPost,
  groupMediasByMedia,
  convertMetricToDetail
} from './utils'

const Container = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, 160px);
  justify-content: center;
  gap: 16px;
  padding-bottom: 12px;
  padding-top: 2px;
`

type PopularMediaWidgetType = ({
  widgetId,
  onMediaClick
}: {
  widgetId: WidgetId
  onMediaClick?: (targetMedia: SelectedMedia) => void
}) => React.ReactElement

const PopularMediaWidget: PopularMediaWidgetType = ({ widgetId, onMediaClick }) => {
  const widgetSetting = useDashboardWidgetSetting<PopularMediaWidgetsType>(widgetId)
  const widgetData = useDashboardWidgetData<AnnotatedMediaMetricResult>(widgetId)

  const { topN, countType } = widgetSetting
  const metrics = retrieveMetrics(widgetData.data[0])

  const topNMedias = useMemo(() => {
    const medias =
      countType === CountType.POSTS ? groupMediasByPost(metrics) : groupMediasByMedia(metrics)
    return medias.slice(0, topN)
  }, [countType, metrics, topN])

  // For media card
  const mediaCardDataSet = useMemo(
    () =>
      topNMedias.map((medias) => ({
        mediaType: medias[0].mediaType,
        mediaUrl: medias[0].mediaLink,
        thumbnailUrl: medias[0].thumbnailUrl || '',
        hasMultipleMedia: medias.length > 1,
        ...convertMetricToMediaCardData(metrics[medias[0].metricIndex])
      })),
    [topNMedias, metrics]
  )

  const handleMediaClick = (index: number) => {
    const activeMedias = index === null ? null : topNMedias[index]
    const activeMetric = activeMedias ? metrics[activeMedias[0].metricIndex] : null
    const mediaModalMedias = activeMedias
      ? activeMedias.map((media) => ({
          type: media.mediaType || 'Image',
          url: media.mediaLink || '',
          thumbnailUrl: media.thumbnailUrl || ''
        }))
      : []
    const mediaModalDetail = activeMetric ? convertMetricToDetail(activeMetric) : {}

    onMediaClick?.({
      mediaModalMedias,
      mediaModalDetail
    })
  }

  return (
    <Container>
      {mediaCardDataSet.map((data, index) => {
        const {
          mediaType,
          mediaUrl,
          thumbnailUrl,
          domain,
          dataStream,
          footer,
          hasMultipleMedia
        } = data
        return (
          <MediaCard
            // eslint-disable-next-line react/no-array-index-key
            key={index}
            mediaType={mediaType}
            mediaUrl={mediaUrl}
            thumbnailUrl={thumbnailUrl}
            domain={domain}
            dataStream={dataStream}
            footer={footer}
            hasMultipleMedia={hasMultipleMedia}
            onClick={() => {
              handleMediaClick(index)
            }}
          />
        )
      })}
    </Container>
  )
}

export default PopularMediaWidget
